import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FieldExpression, NodeExpression, RangeExpression, toString as luceneToString } from 'lucene';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { NgIf, NgFor, DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../../../animations/page-transitions';
import { DATETIME_FORMAT_PATTERN } from '../../../../models/filter-formats';
import { News } from '../../../../models/news-model';
import { NewsService } from '../../../../services/news.service';
import { TranslationService } from '../../../../services/translation.service';
import { OrderBy, OrderByDirection } from '../../../../utils/constant';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'from';

@Component({
	selector: 'copo-news-widget',
	templateUrl: './news-widget.component.html',
	styleUrl: './news-widget.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,

	imports: [TranslateModule, MatButtonModule, RouterLink, NgIf, NgFor, MatIconModule, DatePipe]
})
export class NewspWidgetComponent implements OnInit, OnDestroy {
	newsFields = <const> [
		'id',
		'type',
		'from',
		'to',
		'newsContents',
		'newsContents.newsAttachments',
		'services',
		'audiences',
		'rowCreationBy'
	];

	limit = 3;
	newsList?: News[];
	newsSub?: Subscription;
	currentDate = moment(Date.now()).format(DATETIME_FORMAT_PATTERN);

	constructor(
		private readonly newsServivce: NewsService,
		readonly translationService: TranslationService,
		readonly router: Router,
		readonly activatedRoute: ActivatedRoute
	) {}

	ngOnInit() {
		this.getNews();
	}

	getNews() {
		this.newsSub = this.newsServivce.getNews(
			this.newsFields,
			this.constructFilterNewsWidget(),
			1,
			this.limit,
			new OrderBy(SORT_BY, SORT_WAY),
		)
			.subscribe(value => {
				this.newsList = value;
				const { currentLanguage } = this.translationService;
				this.newsList = this.newsList.map(item => {
					const currentLanguageItem = item.contents
						.find(content => content.languageCode === currentLanguage
							? content : content.languageCode === 'en');
					item.title = currentLanguageItem?.title;
					item.content = this.convertNewsContentToHTML(currentLanguageItem?.content);
					return item;
				});
			});
	}

	constructFilterNewsWidget() {
		const typeFilter: FieldExpression = {
			field: 'type',
			term: 'news',
			quoted: true
		};
		const fromDateFilter: RangeExpression = {
			field: 'from',
			term_min: '*',
			term_max: this.currentDate,
			inclusive: 'both'
		};
		const toDateFilter: RangeExpression = {
			field: 'to',
			term_min: this.currentDate,
			term_max: '*',
			inclusive: 'both'
		};
		const dateFilter: NodeExpression = {
			left: fromDateFilter,
			operator: 'AND',
			right: toDateFilter,
			parenthesized: true
		};
		const newsDateFilter: NodeExpression = {
			left: typeFilter,
			operator: 'AND',
			right: dateFilter,
			parenthesized: true
		};
		return luceneToString(newsDateFilter);
	}

	async viewDetails(id: number) {
		await this.router.navigateByUrl(`/home/news-card/${ id }`);
	}

	ngOnDestroy() {
		if (this.newsSub) {
			this.newsSub.unsubscribe();
		}
	}

	convertNewsContentToHTML(text: string | undefined): string | undefined {
		const urlPattern = /(https?:\/\/\S+)/g;
		// eslint-disable-next-line sonarjs/slow-regex
		const emailPattern = /([\w.-]+@[\w.-]+\.[\w-]+)/g;
		return text?.replace(urlPattern, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
			.replace(emailPattern, '<a href="mailto:$1">$1</a>');
	}
}
